import {
  NumericFormat,
  NumberFormatValues,
  SourceInfo,
} from 'react-number-format'
import store from 'stores/store'
import MaxAmountButton from '@components/shared/MaxAmountButton'
import WalletIcon from '@components/icons/WalletIcon'
import SwapTokenSelect from './SwapTokenSelect'
import { useCallback, useState } from 'react'
import { floorToDecimal, formatCurrencyValue } from 'utils/numbers'
import Decimal from 'decimal.js'
import SearchModal from '@components/modals/SearchModal'
import { TokenMetadataItem } from 'types/token'
import { useLatestSellTokenPrice } from './useLatestTokenPrice'
import { useTranslation } from 'next-i18next'
import RefetchBalancesButton from '@components/shared/RefetchBalancesButton'

const set = store.getState().set

const SIZE_VALUES = ['50', '100'] as const
type SizeValue = (typeof SIZE_VALUES)[number]

const MAX_DIGITS = 11
export const withValueLimit = (values: NumberFormatValues): boolean => {
  return values.floatValue
    ? values.floatValue.toFixed(0).length <= MAX_DIGITS
    : true
}

function setAmountInFormValue(amountIn: string) {
  set((s) => {
    s.swap.amountIn = amountIn
  })
}

const SellInput = ({
  max,
  decimals,
  error,
  setError,
}: {
  max: string
  decimals: number
  error: string | undefined
  setError: (e: string) => void
}) => {
  const { t } = useTranslation('swap')
  const amountInFormValue = store((s) => s.swap.amountIn)
  const inputToken = store((s) => s.swap.inputToken)
  const [showSelectList, setShowSelectList] = useState(false)
  const { lastPrice } = useLatestSellTokenPrice(inputToken.mint)

  const handleAmountInChange = useCallback(
    (e: NumberFormatValues, info: SourceInfo) => {
      if (info.source !== 'event') return

      const value = e.value === '.' ? '0.' : e.value
      setAmountInFormValue(value)
      if (error) {
        setError('')
      }
    },
    [error, setError],
  )

  const handleSizePercentage = useCallback(
    (percentage: '' | SizeValue) => {
      const walletMaxDecimal = new Decimal(max)
      if (walletMaxDecimal.gt(0) && percentage) {
        let amount = walletMaxDecimal.mul(percentage).div(100)
        if (percentage !== '100') {
          amount = floorToDecimal(amount, decimals)
        }
        setAmountInFormValue(amount.toFixed())
      } else {
        setAmountInFormValue('')
      }
      if (error) {
        setError('')
      }
    },
    [max, decimals, error, setError],
  )

  const handleSelect = useCallback(
    async (newInputMint: string, newInputToken?: TokenMetadataItem) => {
      void store
        .getState()
        .actions.setNewInputToken(newInputMint, newInputToken)

      setShowSelectList(false)
    },
    [],
  )

  return (
    <>
      <div className="grid grid-cols-2 rounded-t-xl">
        <div className="col-span-2 mb-2 flex items-center justify-between">
          <p className="text-th-fgd-1">{t('sell')}</p>
          {inputToken.mint ? (
            <div className="flex items-center space-x-2">
              <span className="text-xs">
                <MaxAmountButton
                  decimals={decimals}
                  label={<WalletIcon className="size-3" />}
                  onClick={() => handleSizePercentage('100')}
                  value={max}
                  disabled={max === '0'}
                />
              </span>
              <RefetchBalancesButton />
            </div>
          ) : null}
        </div>
        <div className="col-span-2 mb-2 flex items-center justify-between">
          <SwapTokenSelect
            imageUrl={inputToken.image_url}
            symbol={inputToken.symbol}
            showList={setShowSelectList}
          />
          <SizeButtons
            onClick={handleSizePercentage}
            showClear={!!amountInFormValue}
            sizeValues={SIZE_VALUES}
          />
        </div>
        <div className="col-span-2">
          {/* {loading ? (
          <div className="flex h-12 w-full items-center justify-center  rounded-lg bg-th-input-bkg">
            <Loading />
          </div>
        ) : ( */}
          <div className="flex items-center">
            <NumericFormat
              inputMode="decimal"
              lang="en"
              allowedDecimalSeparators={['.', ',']}
              thousandSeparator=","
              allowNegative={false}
              valueIsNumericString
              decimalScale={decimals || 6}
              name="amount"
              id="amount"
              className="box-border h-10 w-full bg-transparent font-mono text-xl text-th-fgd-1 focus:outline-none md:hover:bg-transparent"
              placeholder="0.00"
              value={amountInFormValue}
              onValueChange={handleAmountInChange}
              isAllowed={withValueLimit}
            />
            <p className="font-mono text-xs text-th-fgd-4">
              {amountInFormValue
                ? lastPrice?.price
                  ? formatCurrencyValue(
                      parseFloat(amountInFormValue) * lastPrice.price,
                    )
                  : '–'
                : '$0.00'}
            </p>
          </div>
        </div>
      </div>
      {showSelectList ? (
        <SearchModal
          isOpen={showSelectList}
          onClose={() => setShowSelectList(false)}
          onResultClick={handleSelect}
          type="inputToken"
        />
      ) : null}
    </>
  )
}

export default SellInput

export const SizeButtons = <
  TValue extends readonly string[],
  TShowClear extends boolean,
>({
  onClick,
  showClear,
  sizeValues,
}: {
  onClick: (
    p: TShowClear extends true ? TValue[number] | '' : TValue[number],
  ) => void
  showClear: TShowClear
  sizeValues: TValue
}) => {
  const { t } = useTranslation('swap')
  const valuesToShow = showClear ? ['', ...sizeValues] : sizeValues
  return (
    <div className="space-x-0.5">
      {valuesToShow.map((v) => (
        <button
          key={v}
          className="h-6 rounded-md bg-th-bkg-3 px-1 font-mono text-xxs focus:outline-none sm:h-7 sm:px-1.5 md:hover:bg-th-bkg-4"
          onClick={() => onClick(v)}
        >
          {v === '' ? t('clear') : v === '100' ? t('max') : `${v}%`}
        </button>
      ))}
    </div>
  )
}
